/**
 * PLANTILLAS de campaña — el "anuncio rápido" de siempre, ya escrito.
 *
 * Cada plantilla trae canales, categoría, copy multilingüe, CTA y destino. Los
 * huecos van marcados con {{campo}} y se rellenan con los `fields` que declara la
 * propia plantilla, así que mandar "nuevo deck" es: elegir plantilla, escribir el
 * nombre y el id, y darle a enviar.
 *
 * El destino SIEMPRE es un deeplink (boostchinese://deck/<id>): la app los
 * resuelve a una pantalla suya en vez de abrir el navegador.
 */

import { LANGS } from "../shared";
import { newBlock } from "./blocks";

/** Destinos internos que entiende la app (utils/deeplinks.js del repo móvil). */
export const DEEPLINKS = [
  { value: "", label: "Sin destino" },
  { value: "boostchinese://deck/", label: "Un deck concreto", needsId: "id del deck", contentType: "deck" },
  { value: "boostchinese://story/", label: "Una historia concreta", needsId: "id de la historia", contentType: "text" },
  { value: "boostchinese://decks", label: "Catálogo de decks" },
  { value: "boostchinese://stories", label: "Catálogo de historias" },
  { value: "boostchinese://new-content", label: "Novedades" },
  { value: "boostchinese://premium", label: "Planes / Premium" },
  { value: "boostchinese://conversations", label: "Conversaciones (soporte)" },
  { value: "boostchinese://stats", label: "Estadísticas" },
  { value: "boostchinese://tones", label: "Juego de tonos" },
  { value: "boostchinese://profile", label: "Perfil" },
];

export const TEMPLATES = [
  {
    "id": "deck-nuevo",
    "contentType": "deck",
    "emoji": "🃏",
    "label": "Nuevo deck",
    "description": "Anuncia un deck recién publicado. Entra al deck desde el propio anuncio.",
    "channels": [
      "inapp",
      "push"
    ],
    "category": "announcement",
    "name": "Anuncio deck · {{nombre}}",
    "fields": [
      {
        "key": "nombre",
        "label": "Nombre del deck",
        "placeholder": "En la farmacia",
        "required": true
      },
      {
        "key": "emoji",
        "label": "Emoji",
        "placeholder": "💊"
      },
      {
        "key": "n",
        "label": "Nº de tarjetas",
        "placeholder": "101"
      },
      {
        "key": "nivel",
        "label": "Nivel",
        "placeholder": "Avanzado"
      },
      {
        "key": "gancho",
        "label": "El gancho (una frase)",
        "placeholder": "resolver una farmacia en China sin señalar con el dedo",
        "required": true
      },
      {
        "key": "id",
        "label": "ID del deck",
        "placeholder": "6436068f8f8bfff0bc4c1f11",
        "hint": "Sale en la URL del deck en el backoffice"
      }
    ],
    "title": {
      "es": "Nuevo deck: {{nombre}} {{emoji}}",
      "en": "New deck: {{nombre}} {{emoji}}",
      "fr": "Nouveau deck : {{nombre}} {{emoji}}",
      "pt": "Novo deck: {{nombre}} {{emoji}}",
      "ru": "Новая колода: {{nombre}} {{emoji}}"
    },
    "blocks": [
      {
        "type": "paragraph",
        "text": {
          "es": "<b>{{n}} tarjetas</b> para {{gancho}}.",
          "en": "<b>{{n}} cards</b> to {{gancho}}.",
          "fr": "<b>{{n}} cartes</b> pour {{gancho}}.",
          "pt": "<b>{{n}} cartões</b> para {{gancho}}.",
          "ru": "<b>{{n}} карточек</b>: {{gancho}}."
        }
      },
      {
        "type": "paragraph",
        "text": {
          "es": "Nivel <b>{{nivel}}</b> · con audio nativo en todas las tarjetas.",
          "en": "<b>{{nivel}}</b> level · native audio on every card.",
          "fr": "Niveau <b>{{nivel}}</b> · audio natif sur toutes les cartes.",
          "pt": "Nível <b>{{nivel}}</b> · com áudio nativo em todos os cartões.",
          "ru": "Уровень <b>{{nivel}}</b> · озвучка носителем на каждой карточке."
        }
      }
    ],
    "pushBody": {
      "es": "{{n}} tarjetas nuevas para {{gancho}}. Nivel {{nivel}}.",
      "en": "{{n}} new cards to {{gancho}}. {{nivel}} level.",
      "fr": "{{n}} nouvelles cartes pour {{gancho}}. Niveau {{nivel}}.",
      "pt": "{{n}} cartões novos para {{gancho}}. Nível {{nivel}}.",
      "ru": "{{n}} новых карточек: {{gancho}}. Уровень {{nivel}}."
    },
    "linkUrl": "boostchinese://deck/{{id}}",
    "linkTitle": {"es": "Abrir el deck", "en": "Open the deck", "fr": "Ouvrir le deck", "pt": "Abrir o deck", "ru": "Открыть колоду"},
    "imageUrl": "{{imagen}}"
  },
  {
    "id": "historia-nueva",
    "contentType": "text",
    "emoji": "📖",
    "label": "Nueva historia",
    "description": "Anuncia una historia nueva del catálogo y la abre dentro de la app.",
    "channels": [
      "inapp",
      "push"
    ],
    "category": "announcement",
    "name": "Anuncio historia · {{nombre}}",
    "fields": [
      {
        "key": "nombre",
        "label": "Título de la historia",
        "placeholder": "El efecto «mukbang»",
        "required": true
      },
      {
        "key": "emoji",
        "label": "Emoji",
        "placeholder": "🍜"
      },
      {
        "key": "gancho",
        "label": "El gancho (una o dos frases)",
        "placeholder": "Millones de personas ven a desconocidos comer en directo. En China el fenómeno tiene nombre, reglas… y polémica.",
        "required": true
      },
      {
        "key": "n",
        "label": "Nº de capítulos",
        "placeholder": "9"
      },
      {
        "key": "nivel",
        "label": "Nivel",
        "placeholder": "Intermedio"
      },
      {
        "key": "id",
        "label": "ID de la historia",
        "placeholder": "6788cfe32768b8bf3ff52c68"
      }
    ],
    "title": {
      "es": "Nueva historia: {{nombre}} {{emoji}}",
      "en": "New story: {{nombre}} {{emoji}}",
      "fr": "Nouvelle histoire : {{nombre}} {{emoji}}",
      "pt": "Nova história: {{nombre}} {{emoji}}",
      "ru": "Новая история: {{nombre}} {{emoji}}"
    },
    "blocks": [
      {
        "type": "paragraph",
        "text": {
          "es": "{{gancho}}",
          "en": "{{gancho}}",
          "fr": "{{gancho}}",
          "pt": "{{gancho}}",
          "ru": "{{gancho}}"
        }
      },
      {
        "type": "paragraph",
        "text": {
          "es": "Nivel <b>{{nivel}}</b> · {{n}} capítulos con audio y traducción al toque.",
          "en": "<b>{{nivel}}</b> level · {{n}} chapters with audio and tap-to-translate.",
          "fr": "Niveau <b>{{nivel}}</b> · {{n}} chapitres avec audio et traduction au toucher.",
          "pt": "Nível <b>{{nivel}}</b> · {{n}} capítulos com áudio e tradução ao toque.",
          "ru": "Уровень <b>{{nivel}}</b> · {{n}} глав с озвучкой и переводом по тапу."
        }
      }
    ],
    "pushBody": {
      "es": "{{gancho}} Nivel {{nivel}}, {{n}} capítulos.",
      "en": "{{gancho}} {{nivel}} level, {{n}} chapters.",
      "fr": "{{gancho}} Niveau {{nivel}}, {{n}} chapitres.",
      "pt": "{{gancho}} Nível {{nivel}}, {{n}} capítulos.",
      "ru": "{{gancho}} Уровень {{nivel}}, глав: {{n}}."
    },
    "linkUrl": "boostchinese://story/{{id}}",
    "linkTitle": {"es": "Leer la historia", "en": "Read the story", "fr": "Lire l'histoire", "pt": "Ler a história", "ru": "Читать историю"},
    "imageUrl": "{{imagen}}"
  },
  {
    "id": "push-racha",
    "emoji": "🔥",
    "label": "Push de vuelta",
    "description": "Solo push, corta, para quien lleva días sin jugar. Abre el home.",
    "channels": [
      "push"
    ],
    "category": "marketing",
    "name": "Push vuelta · {{motivo}}",
    "fields": [
      {
        "key": "motivo",
        "label": "Motivo (interno)",
        "placeholder": "inactivos 30 días",
        "required": true
      }
    ],
    "title": {
      "es": "Tu chino te echa de menos 🥟",
      "en": "Your Chinese misses you 🥟",
      "fr": "Ton chinois s'ennuie de toi 🥟",
      "pt": "O teu chinês tem saudades 🥟",
      "ru": "Твой китайский скучает 🥟"
    },
    "blocks": [],
    "pushBody": {
      "es": "Cinco minutos hoy valen más que una hora el domingo. ¿Repasamos lo que ya sabes?",
      "en": "Five minutes today beat an hour on Sunday. Shall we review what you already know?",
      "fr": "Cinq minutes aujourd'hui valent mieux qu'une heure dimanche. On révise ?",
      "pt": "Cinco minutos hoje valem mais do que uma hora no domingo. Vamos rever?",
      "ru": "Пять минут сегодня лучше часа в воскресенье. Повторим то, что уже знаешь?"
    },
    "linkUrl": "boostchinese://decks",
    "linkTitle": {"es": "Seguir aprendiendo", "en": "Keep learning", "fr": "Continuer à apprendre", "pt": "Continuar a aprender", "ru": "Продолжить учёбу"},
    "imageUrl": ""
  },
  {
    "id": "mail-bao",
    "emoji": "✉️",
    "label": "Correo de Bāo",
    "description": "La newsletter larga en la que Bāo cuenta qué es Boost y qué ha cambiado. 5 secciones con capturas, en 5 idiomas. Lista para enviar.",
    "channels": [
      "mail"
    ],
    "category": "marketing",
    "mailTemplate": "bao",
    "name": "Correo de Bāo · {{motivo}}",
    "fields": [
      {
        "key": "motivo",
        "label": "Motivo (interno)",
        "placeholder": "re-engagement agosto",
        "required": true
      }
    ],
    "title": {
      "en": "Hi, it's Bāo. Long time no see!",
      "es": "Hola, soy Bāo. ¡Cuánto tiempo sin verte!",
      "fr": "Salut, c'est Bāo. Ça fait longtemps !",
      "pt": "Olá, sou o Bāo. Há quanto tempo!",
      "ru": "Привет, это Бао. Давно не виделись!"
    },
    "blocks": [
      {
        "type": "paragraph",
        "text": {
          "en": "I'm the little bāozi who lives inside BoostChinese — my job is to make sure your Chinese sticks. While you were away, we rebuilt half the app around one idea: less friction, more 中文. Here are the five things I can't wait to show you.",
          "es": "Soy el baozi que vive dentro de BoostChinese — mi trabajo es que el chino se te quede. Mientras no estabas, hemos reconstruido media app con una sola idea: menos fricción, más 中文. Estas son las cinco cosas que estoy deseando enseñarte.",
          "fr": "Je suis le petit bāozi qui vit dans BoostChinese — mon travail, c'est que ton chinois reste gravé. Pendant ton absence, on a reconstruit la moitié de l'app autour d'une seule idée : moins de friction, plus de 中文. Voici les cinq nouveautés que j'ai hâte de te montrer.",
          "pt": "Sou o baozi que vive dentro do BoostChinese — o meu trabalho é fazer com que o teu chinês fique. Enquanto estiveste fora, reconstruímos metade da app com uma só ideia: menos fricção, mais 中文. Aqui estão as cinco coisas que mal posso esperar para te mostrar.",
          "ru": "Я — баоцзы, который живёт внутри BoostChinese. Моя работа — чтобы китайский оставался с тобой. Пока тебя не было, мы переделали половину приложения ради одной идеи: меньше трения, больше 中文. Вот пять новинок, которые я очень хочу показать."
        }
      }
    ],
    "linkUrl": "https://boostchinese.com/download",
    "linkTitle": {"es": "Volver a Boost", "en": "Back to Boost", "fr": "Revenir sur Boost", "pt": "Voltar ao Boost", "ru": "Вернуться в Boost"},
    "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/bao-hero.png",
    "sections": [
      {
        "title": {
          "en": "Your new Home",
          "es": "Tu nuevo Home",
          "fr": "Ton nouvel accueil",
          "pt": "O teu novo ecrã inicial",
          "ru": "Твой новый главный экран"
        },
        "body": {
          "en": "Everything that matters, the moment you open the app: your streak with its calendar, your brain with the words you're about to forget, one-tap game modes, fresh decks and the topic of the week. No digging through menus — your next study session is always one tap away.",
          "es": "Todo lo que importa nada más abrir la app: tu racha con su calendario, tu cerebro con las palabras que estás a punto de olvidar, los modos de juego a un toque, decks recién publicados y el tema de la semana. Sin bucear por menús — tu siguiente sesión de estudio siempre está a un toque.",
          "fr": "Tout ce qui compte, dès l'ouverture : ta série avec son calendrier, ton cerveau avec les mots que tu es sur le point d'oublier, les modes de jeu en un geste, des decks tout frais et le thème de la semaine. Fini de fouiller les menus — ta prochaine session d'étude est toujours à un tap.",
          "pt": "Tudo o que importa assim que abres a app: a tua sequência com o seu calendário, o teu cérebro com as palavras que estás quase a esquecer, os modos de jogo a um toque, decks acabados de publicar e o tema da semana. Sem escavar menus — a tua próxima sessão de estudo está sempre a um toque.",
          "ru": "Всё важное — сразу при открытии: твоя серия с календарём, твой «мозг» со словами, которые вот-вот забудутся, игровые режимы в один тап, свежие колоды и тема недели. Никаких блужданий по меню — следующая тренировка всегда в одном касании."
        },
        "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/screenshot-home.png"
      },
      {
        "title": {
          "en": "A catalog you can actually browse",
          "es": "Un catálogo por el que da gusto pasear",
          "fr": "Un catalogue où on aime flâner",
          "pt": "Um catálogo onde apetece passear",
          "ru": "Каталог, по которому приятно гулять"
        },
        "body": {
          "en": "Decks and stories, reorganized by category, HSK level and topic — food, travel, business, mythology… — with instant search and covers that tell you what's inside. Each card shows your real progress, so you always know where you left off. And it flies, even on the fullest shelves.",
          "es": "Decks e historias reorganizados por categoría, nivel HSK y tema — comida, viajes, negocios, mitología… — con buscador instantáneo y portadas que te cuentan qué hay dentro. Cada tarjeta enseña tu progreso real, así que siempre sabes dónde lo dejaste. Y vuela, hasta con las estanterías más llenas.",
          "fr": "Decks et histoires réorganisés par catégorie, niveau HSK et thème — cuisine, voyages, business, mythologie… — avec recherche instantanée et des couvertures qui annoncent la couleur. Chaque carte affiche ta vraie progression : tu sais toujours où tu t'étais arrêté. Et ça file, même sur les étagères les plus pleines.",
          "pt": "Decks e histórias reorganizados por categoria, nível HSK e tema — comida, viagens, negócios, mitologia… — com pesquisa instantânea e capas que dizem o que há lá dentro. Cada cartão mostra o teu progresso real, por isso sabes sempre onde ficaste. E voa, mesmo com as prateleiras mais cheias.",
          "ru": "Колоды и истории заново разложены по категориям, уровням HSK и темам — еда, путешествия, бизнес, мифология… — с мгновенным поиском и обложками, по которым видно содержание. На каждой карточке — твой реальный прогресс: всегда понятно, где ты остановился. И всё летает, даже на самых полных полках."
        },
        "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/screenshot-decks.png"
      },
      {
        "title": {
          "en": "New ways to play (and to remember)",
          "es": "Nuevas formas de jugar (y de recordar)",
          "fr": "De nouvelles façons de jouer (et de retenir)",
          "pt": "Novas formas de jogar (e de recordar)",
          "ru": "Новые способы играть (и запоминать)"
        },
        "body": {
          "en": "Tones to train your ear, sound pairs to match what you hear with what you read, pinyin grids, and flashcards with native video. Pick any deck, pick any mode, and the algorithm quietly schedules each word for the exact moment before you'd forget it. That part is my favourite.",
          "es": "Tonos para entrenar el oído, parejas de sonidos para unir lo que oyes con lo que lees, rejilla de pinyin y flashcards con vídeo nativo. Elige cualquier deck, elige cualquier modo, y el algoritmo programa en silencio cada palabra justo para el momento antes de que la olvides. Esa parte es mi favorita.",
          "fr": "Des tons pour entraîner l'oreille, des paires sonores pour relier ce que tu entends à ce que tu lis, des grilles de pinyin et des flashcards en vidéo. Choisis un deck, choisis un mode, et l'algorithme programme discrètement chaque mot juste avant que tu ne l'oublies. C'est ma partie préférée.",
          "pt": "Tons para treinar o ouvido, pares de sons para ligar o que ouves ao que lês, grelhas de pinyin e flashcards com vídeo nativo. Escolhe qualquer deck, escolhe qualquer modo, e o algoritmo agenda em silêncio cada palavra para o momento exato antes de a esqueceres. Essa parte é a minha favorita.",
          "ru": "Тоны — тренировать слух, звуковые пары — связывать услышанное с прочитанным, сетка пиньиня и карточки с видео носителей. Выбери колоду, выбери режим — а алгоритм незаметно подставит каждое слово ровно в тот момент, когда ты вот-вот его забудешь. Это моя любимая часть."
        },
        "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/screenshot-game.png"
      },
      {
        "title": {
          "en": "I'll give you the tour myself",
          "es": "El tour te lo doy yo en persona",
          "fr": "La visite guidée, c'est moi qui te la fais",
          "pt": "A visita guiada faço-a eu em pessoa",
          "ru": "Экскурсию проведу я лично"
        },
        "body": {
          "en": "So much changed that I now walk you through it myself: eleven short stops where you actually play a round, save your first word and watch your brain fill up — not a slideshow. Finish the tour and there's a little premium gift waiting at the end. I won't say more. 😉",
          "es": "Ha cambiado tanto que ahora te lo enseño yo mismo: once paradas cortas en las que juegas una partida de verdad, guardas tu primera palabra y ves cómo se llena tu cerebro — nada de diapositivas. Termina el tour y al final te espera un regalito premium. No digo más. 😉",
          "fr": "Tout a tellement changé que je te fais visiter moi-même : onze arrêts courts où tu joues une vraie partie, tu sauvegardes ton premier mot et tu regardes ton cerveau se remplir — pas un diaporama. Termine la visite et un petit cadeau premium t'attend à la fin. Je n'en dis pas plus. 😉",
          "pt": "Mudou tanto que agora sou eu que te mostro tudo: onze paragens curtas onde jogas uma partida a sério, guardas a tua primeira palavra e vês o teu cérebro a encher-se — nada de diapositivos. Termina o tour e há um presentinho premium à tua espera no fim. Não digo mais. 😉",
          "ru": "Изменилось столько, что теперь я сам всё показываю: одиннадцать коротких остановок, где ты реально играешь раунд, сохраняешь первое слово и видишь, как наполняется твой «мозг» — никаких слайдов. Пройди тур до конца — там ждёт небольшой премиум-подарок. Больше не скажу. 😉"
        },
        "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/screenshot-tour.png"
      },
      {
        "title": {
          "en": "Faster, smoother — and it shows",
          "es": "Más rápida, más fluida — y se nota",
          "fr": "Plus rapide, plus fluide — et ça se voit",
          "pt": "Mais rápida, mais fluida — e nota-se",
          "ru": "Быстрее, плавнее — и это видно"
        },
        "body": {
          "en": "Under the hood we rebuilt the engine: the app opens faster, scrolls like silk and sips memory. And the new stats turn your effort into something you can see — hours studied, words mastered, week by week, month by month. Come back for a few days and watch the curve bend. I'll be there, cheering.",
          "es": "Por debajo hemos reconstruido el motor: la app abre más rápido, el scroll va como la seda y gasta mucha menos memoria. Y las nuevas estadísticas convierten tu esfuerzo en algo que se ve — horas estudiadas, palabras dominadas, semana a semana, mes a mes. Vuelve unos días y mira cómo se dobla la curva. Yo estaré ahí, animando.",
          "fr": "Sous le capot, on a reconstruit le moteur : l'app s'ouvre plus vite, le scroll est soyeux et la mémoire fond. Et les nouvelles stats transforment ton effort en quelque chose de visible — heures d'étude, mots maîtrisés, semaine après semaine, mois après mois. Reviens quelques jours et regarde la courbe se plier. Je serai là, à t'encourager.",
          "pt": "Por baixo reconstruímos o motor: a app abre mais depressa, o scroll é seda e a memória rende muito mais. E as novas estatísticas transformam o teu esforço em algo visível — horas estudadas, palavras dominadas, semana a semana, mês a mês. Volta uns dias e vê a curva a dobrar-se. Eu estarei lá, a torcer por ti.",
          "ru": "Под капотом мы переписали движок: приложение открывается быстрее, скролл — как шёлк, памяти нужно куда меньше. А новая статистика превращает твои усилия во что-то видимое — часы занятий, освоенные слова, неделя за неделей, месяц за месяцем. Вернись на несколько дней — и увидишь, как изгибается кривая. Я буду рядом и буду болеть за тебя."
        },
        "imageUrl": "https://media.boostchinese.com/boostchinesepublic/campaigns/assets/screenshot-stats.png"
      }
    ]
  }
];

/** Sustituye {{campo}} por lo que el admin haya escrito (o lo deja en blanco). */
const fill = (text, values) =>
  String(text || "").replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? "");

const fillMap = (map, values) => {
  const out = {};
  LANGS.forEach((l) => {
    out[l] = map[l] ? fill(map[l], values) : "";
  });
  return out;
};

/** [{type, text:{lang:…}}] → bloques reales del editor, ya interpolados. */
const buildBlocks = (spec, values) =>
  (spec || []).map((b) =>
    newBlock(b.type, {
      text: b.text ? fillMap(b.text, values) : {},
      url: b.url ? fill(b.url, values) : "",
      ...(b.align ? { align: b.align } : {}),
      ...(b.width ? { width: b.width } : {}),
    })
  );

/**
 * Plantilla + valores → parche de estado para el editor. Devuelve solo lo que la
 * plantilla define, para no pisar lo que el admin ya tuviera puesto.
 */
export const applyTemplate = (template, values = {}) => {
  const patch = {
    name: fill(template.name, values),
    channels: [...template.channels],
    category: template.category,
    titles: fillMap(template.title, values),
    blocks: buildBlocks(template.blocks, values),
    pushBodies: template.pushBody ? fillMap(template.pushBody, values) : null,
    linkUrl: fill(template.linkUrl, values),
    // El CTA va traducido como el resto del mensaje.
    linkTitles: fillMap(template.linkTitle, values),
    imageUrl: fill(template.imageUrl, values),
    mailTemplate: template.mailTemplate || "",
    sections: (template.sections || []).map((s) => ({
      title: fillMap(s.title, values),
      body: fillMap(s.body, values),
      imageUrl: s.imageUrl || "",
    })),
  };
  return patch;
};
